import React, { useState } from "react";
import { Button, NumberField, SystemMessage, Text } from "@shoptet/ui";
import { Trans, useTranslation } from "react-i18next";
import {
  AdSystemT,
  useChangeMonthlyBudgetMutation,
  useOrganizationMonthlyBudgetQuery,
} from "../../graphql/generated/graphql";
import { useBudgetCreditCheck } from "../../hooks/useBudgetCreditCheck";
import { useHandleApolloError } from "../../hooks/useHandleApolloError";
import { formatCurrencyUnit, formatMoney } from "../../i18n/formatNumbers";
import { useAppSettings } from "../../providers/appSettingsProvider";
import { formatBudgetInput } from "../../utils/formatBudgetInput";
import { getDefaultMinimumMonthlyBudget } from "../../utils/getDefaultMinimumMonthlyBudget";
import { getMaximumMonthlyBudget } from "../../utils/getMaximumMonthlyBudget";
import { CreditModal } from "../Credits/Modal";
import { CheckmarkIcon, InsightIcon } from "../Icons";
import { SmallLoader } from "../SmallLoader";

type MonthlyBudgetPropsT = {
  adSystem?: AdSystemT;
  afterEditButton?: boolean;
  hideButtons?: boolean;
  hideTooltip?: boolean;
  initialMonthlyBudget?: number;
  onBudgetStateChange?: (budget: number, isValid: boolean) => void;
  onComplete?: () => void;
  onTopUpCredit?: (enteredBudget: number, missingCredit: number) => void;
  saveRef?: React.MutableRefObject<(() => Promise<void>) | null>;
  showCurrentBudget?: boolean;
  useRecommendedMonthlyBudget?: boolean;
};

const TEST_NUMBER = /^\d+[\.,]?\d*$/;

export const MonthlyBudget = ({
  adSystem = AdSystemT.GoogleAdsT,
  afterEditButton,
  hideButtons,
  hideTooltip,
  initialMonthlyBudget,
  onBudgetStateChange,
  onComplete,
  onTopUpCredit,
  saveRef,
  showCurrentBudget,
  useRecommendedMonthlyBudget,
}: MonthlyBudgetPropsT) => {
  const { t } = useTranslation("translation", { keyPrefix: "Components.MonthlyBudget" });
  const { currency: defaultCurrency } = useAppSettings();
  const [isSaved, setIsSaved] = useState(false);
  const [showCreditModal, setShowCreditModal] = useState(false);
  const handleApolloError = useHandleApolloError();

  const [originalState, setOriginalState] = useState({
    currency: defaultCurrency,
    monthlyBudget: "0",
  });

  const [state, setState] = useState({
    currency: defaultCurrency,
    monthlyBudget: "0",
  });

  const { data: organizationMonthlyBudgetData, loading } = useOrganizationMonthlyBudgetQuery({
    onCompleted: (data) => {
      // Get monthly budget based on ad system
      const adSettingBudget =
        adSystem === AdSystemT.GoogleAdsT
          ? data.organization?.googleAdsSetting?.monthlyBudget
          : data.organization?.metaAdsSetting?.monthlyBudget;

      const newState = {
        monthlyBudget: formatMoney({ amount: adSettingBudget?.amount || 0 }),
        currency: adSettingBudget?.currency || defaultCurrency,
      };
      setState(
        initialMonthlyBudget ? { ...newState, monthlyBudget: formatMoney({ amount: initialMonthlyBudget }) } : newState
      );
      setOriginalState(newState);
    },
  });

  const [saveFailed, setSaveFailed] = useState(false);
  const [changeMonthlyBudget, { error: mutationError, loading: loadingMutation }] = useChangeMonthlyBudgetMutation({
    onCompleted: (data) => {
      if (data.changeMonthlyBudget?.errors?.length) {
        setSaveFailed(true);
        return;
      }
      setSaveFailed(false);
      // Update originalState to reflect the saved value
      setOriginalState({ ...state });
      setIsSaved(true);
      setTimeout(() => {
        setIsSaved(false);
        if (typeof onComplete === "function") {
          onComplete();
        }
      }, 2000);
    },
  });

  const recommendedMonthlyBudgetAmount =
    organizationMonthlyBudgetData?.organization?.recommendedMonthlyBudget?.amount || 0;
  const defaultMinimumMonthlyBudget = getDefaultMinimumMonthlyBudget(defaultCurrency);
  const minimalMonthlyBudget = useRecommendedMonthlyBudget
    ? recommendedMonthlyBudgetAmount
    : defaultMinimumMonthlyBudget;

  const maximalMonthlyBudget = getMaximumMonthlyBudget(state.currency);

  const monthlyBudgetString = state.monthlyBudget.replace(/\s/g, "").replace(",", ".");
  const isBudgetValidNumber = !!monthlyBudgetString.match(TEST_NUMBER);
  const monthlyBudget = parseFloat(isBudgetValidNumber ? monthlyBudgetString : originalState.monthlyBudget);
  const noChanges = originalState.monthlyBudget == state.monthlyBudget;
  const underLimit = monthlyBudget < minimalMonthlyBudget;
  const overLimit = monthlyBudget > maximalMonthlyBudget;

  const originalMonthlyBudget = parseFloat(originalState.monthlyBudget.replace(/\s/g, "").replace(",", ".")) || 0;
  const { insufficientCredit, missingCredit } = useBudgetCreditCheck(adSystem, monthlyBudget, originalMonthlyBudget);

  const isValid = isBudgetValidNumber && !underLimit && !overLimit && !insufficientCredit;

  React.useEffect(() => {
    if (initialMonthlyBudget) {
      setState((prev) => ({ ...prev, monthlyBudget: formatMoney({ amount: initialMonthlyBudget }) }));
    }
  }, [initialMonthlyBudget]);

  React.useEffect(() => {
    onBudgetStateChange?.(monthlyBudget, isValid);
  }, [monthlyBudget, isValid, onBudgetStateChange]);

  if (loading) {
    return <SmallLoader />;
  }

  let error;
  if (!isBudgetValidNumber) {
    error = t("Not a valid number");
  } else if (underLimit) {
    error = t("Must be greater than", {
      minimalAmount: formatMoney({
        amount: minimalMonthlyBudget,
        currency: state.currency,
      }),
    });
  } else if (overLimit) {
    error = t("Must be lower than", {
      maximalAmount: formatMoney({
        amount: maximalMonthlyBudget,
        currency: state.currency,
      }),
    });
  } else if (insufficientCredit) {
    error = t("Insufficient credit for budget change");
  }

  const handleSave = async () => {
    try {
      await changeMonthlyBudget({
        variables: { adSystem, currency: state.currency, monthlyBudget },
      });
    } catch (_error) {
      handleApolloError(_error);
    }
  };

  if (saveRef) {
    saveRef.current = async () => {
      const result = await changeMonthlyBudget({
        variables: { adSystem, currency: state.currency, monthlyBudget },
      });
      const saveErrors = result.data?.changeMonthlyBudget?.errors;
      if (saveErrors?.length) {
        throw new Error(saveErrors.join(", "));
      }
    };
  }

  const addon = (
    <div style={{ marginTop: "4px" }}>
      {!afterEditButton && noChanges && !isSaved ? (
        <span />
      ) : (
        <span>
          <Button
            data-test-id="monthly-budget-save-button"
            disabled={!isValid || loadingMutation || isSaved}
            variant="action"
            onClick={handleSave}
          >
            {isSaved ? (
              <span style={{ display: "flex", alignItems: "center" }}>
                <CheckmarkIcon /> &nbsp; {t("Saved")}
              </span>
            ) : (
              t("Save")
            )}
          </Button>
          {afterEditButton && !isSaved && (
            <Button variant="secondary" onClick={onComplete}>
              {t("Cancel")}
            </Button>
          )}
        </span>
      )}
    </div>
  );

  const budgetField = (
    <NumberField
      error={error}
      label={t("Label")}
      labelAfter={formatCurrencyUnit(state.currency)}
      labelHidden={showCurrentBudget}
      name="monthly_budget"
      tooltip={hideTooltip ? undefined : t("Tooltip")}
      value={isBudgetValidNumber ? parseFloat(monthlyBudgetString) : null}
      onChange={(value) =>
        setState({ ...state, monthlyBudget: value === null ? "" : formatBudgetInput(value.toString()) })
      }
    />
  );

  const currentBudgetAmount =
    adSystem === AdSystemT.GoogleAdsT
      ? organizationMonthlyBudgetData?.organization?.googleAdsSetting?.monthlyBudget?.amount
      : organizationMonthlyBudgetData?.organization?.metaAdsSetting?.monthlyBudget?.amount;

  return (
    <>
      {showCurrentBudget ? (
        <div
          className="monthly-budget monthly-budget--grid"
          style={{
            display: "grid",
            gridTemplateColumns: "max-content max-content",
            columnGap: "24px",
            rowGap: "24px",
            justifyContent: "center",
            alignItems: "baseline",
          }}
        >
          <Text>{t("Current monthly budget")}</Text>
          <Text appearance="strong-md">
            <span style={{ display: "block", textAlign: "right" }}>
              {`${formatMoney({ amount: currentBudgetAmount || 0 })} ${formatCurrencyUnit(state.currency)}`}
            </span>
          </Text>
          <Text>{t("Label")}</Text>
          <div style={{ display: "flex", gap: "2px" }}>
            {budgetField}
            {!hideButtons && addon}
          </div>
        </div>
      ) : (
        <div className="monthly-budget" style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ display: "flex", justifyContent: "center", gap: "2px" }}>
            {budgetField}
            {!hideButtons && addon}
          </div>
        </div>
      )}
      {insufficientCredit && !overLimit && (
        <>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "16px",
              padding: "16px",
              marginTop: "16px",
              border: "2px solid var(--app-warning)",
              borderRadius: "var(--app-radius-box)",
            }}
          >
            <InsightIcon color="var(--app-warning)" />
            <p style={{ margin: 0, flex: 1 }}>
              <Trans
                components={{ b: <b /> }}
                i18nKey="Budget increase raises daily ad costs"
                t={t}
                values={{
                  missingAmount: `${formatMoney({ amount: Math.ceil(missingCredit) })} ${formatCurrencyUnit(
                    state.currency
                  )}`,
                }}
              />
            </p>
            <Button
              data-test-id="monthly-budget-top-up-credit-button"
              variant="primary"
              onClick={() =>
                onTopUpCredit ? onTopUpCredit(monthlyBudget, Math.ceil(missingCredit)) : setShowCreditModal(true)
              }
            >
              {t("Top up credit")}
            </Button>
          </div>
          {!onTopUpCredit && showCreditModal && (
            <CreditModal
              initialCustomAmount={Math.ceil(missingCredit)}
              isOpen
              onClose={() => setShowCreditModal(false)}
            />
          )}
        </>
      )}
      {(mutationError || saveFailed) && (
        <div style={{ width: "100%" }}>
          <SystemMessage description={t("there was a problem saving monthly budget")} level="error" />
        </div>
      )}
    </>
  );
};
